import { addDays } from 'date-fns/addDays';
import { type Registry as Services, service } from '@ember/service';
import { formatLocalDate } from 'uplisting-frontend/utils/date';
import BaseRepositoryService from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type CalendarAdapter from 'uplisting-frontend/adapters/calendar';
import { CALENDAR_DATES_PER_PAGE_LARGE } from 'uplisting-frontend/services/calendar/utils';

interface IDateRange {
  start_date: string;
  end_date: string;
}

interface IFetchCalendarParams extends IDateRange {
  include: string;
  property_ids?: string[];
}

export enum CalendarPart {
  occasions = 'occasions',
  enquiries = 'enquiries',
  changeovers = 'changeovers',
  rates = 'rates',
}

export const ALL_CALENDAR_PARTS = [
  CalendarPart.occasions,
  CalendarPart.enquiries,
  CalendarPart.changeovers,
  CalendarPart.rates,
];

export interface ICalendarLoadOptions {
  startDate: Date;
  endDate?: Date;
  ids?: string;
  isMultiUnit?: boolean;
  parts?: CalendarPart[];
}

interface ICalendarLoadDataOptions extends ICalendarLoadOptions {
  withTasks?: boolean;
}

const includes: [string, CalendarPart][] = [
  ['rates', CalendarPart.rates],
  ['rates.property', CalendarPart.rates],
  ['bookings', CalendarPart.occasions],
  ['bookings.price', CalendarPart.occasions],
  ['booking_requests', CalendarPart.occasions],
  ['enquiries', CalendarPart.enquiries],
  ['bookings.multi_unit', CalendarPart.occasions],
  ['bookings.airbnb_official_alteration_requests', CalendarPart.occasions],
  ['changeovers', CalendarPart.changeovers],
  ['unavailabilities', CalendarPart.occasions],
  ['changeovers.booking', CalendarPart.changeovers],
  ['changeovers.multi_unit', CalendarPart.changeovers],
];

function getDateRange({
  startDate,
  endDate = addDays(startDate, CALENDAR_DATES_PER_PAGE_LARGE - 1),
}: Pick<ICalendarLoadOptions, 'startDate' | 'endDate'>): IDateRange {
  return {
    start_date: formatLocalDate(startDate),
    end_date: formatLocalDate(endDate),
  };
}

function getFetchCalendarParams(
  options: ICalendarLoadOptions,
): IFetchCalendarParams {
  const { ids, parts = ALL_CALENDAR_PARTS } = options;

  const params: IFetchCalendarParams = {
    ...getDateRange(options),
    include: includes
      .filter(([, part]) => parts.includes(part))
      .map(([name]) => name)
      .join(','),
  };

  if (ids) {
    params[options.isMultiUnit ? 'multi_unit_ids' : 'property_ids'] =
      ids.split(',');
  }

  return params;
}

export default class CalendarRepositoryService extends BaseRepositoryService<
  ModelRegistry['calendar']
> {
  @service featureFlags!: Services['feature-flags'];

  @service('repositories/task') taskRepository!: Services['repositories/task'];

  recordName = 'calendar';
  implementMethods = ['query'];

  #tasksInFlight = new Map<string, Promise<boolean>>();

  public async loadData(options: ICalendarLoadDataOptions): Promise<void> {
    const params = getFetchCalendarParams(options);

    await Promise.all([
      this.query(params),
      options.withTasks ? this.#loadTasks(params) : undefined,
    ]);
  }

  public async fetchData(
    options: ICalendarLoadOptions,
    signal: AbortSignal,
  ): Promise<object> {
    const adapter = this.store.adapterFor('calendar') as CalendarAdapter;

    try {
      return await adapter.fetchCalendar(
        getFetchCalendarParams(options),
        signal,
      );
    } catch (error) {
      if (!signal.aborted) {
        this.sentry.captureException(error, {
          tags: { repository: this.recordName, operation: 'fetchData' },
        });
      }

      throw error;
    }
  }

  public loadTasks(startDate: Date, endDate?: Date): Promise<boolean> {
    return this.#loadTasks(getDateRange({ startDate, endDate }));
  }

  public push(payload: object): void {
    this.store.pushPayload('calendar', payload);
  }

  #loadTasks(range: IDateRange): Promise<boolean> {
    if (!this.featureFlags.gate.enabled('task_centre')) {
      return Promise.resolve(true);
    }

    const key = `${range.start_date}:${range.end_date}`;

    let promise = this.#tasksInFlight.get(key);

    if (!promise) {
      promise = this.taskRepository
        .forDates(range.start_date, range.end_date)
        // Tasks are an overlay on the calendar — a failed fetch (already reported by the
        // repository) must not reject the combined load and leave the whole grid unbuilt.
        .then(
          () => true,
          () => false,
        )
        .finally(() => this.#tasksInFlight.delete(key));

      this.#tasksInFlight.set(key, promise);
    }

    return promise;
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/calendar': CalendarRepositoryService;
  }
}
